/**
 * <gift-pouch> is the optional "Schmuckbeutel dazu" add-on on the PDP.
 *
 * Ticking the checkbox reveals a swipeable row of pouch cards; picking one
 * only marks it as selected - nothing reaches the cart at that point. The
 * selected pouch is added right after this product's own "In den Warenkorb"
 * button succeeds: ProductForm (main.js) dispatches "on:cart:add", and this
 * file listens for that on document, then posts the pouch to /cart/add.js.
 *
 * While a pouch is selected, the main add-to-cart button shows its price as
 * a "(+9,00 €)" suffix, so the full cost of a click is visible beforehand.
 *
 * Both line items carry the same hidden group id, so the order (and the cart,
 * see cart-item-wrapper.liquid) knows which pouch belongs to which product:
 * the main product as "_gift_pouch_group" via a hidden input in its own form,
 * the pouch as "_gift_pouch_for" in its /cart/add.js request.
 */

const GROUP_PROPERTY = 'properties[_gift_pouch_group]';


/** Minimal money formatter (the classic Shopify theme snippet). */
function formatMoney(cents, format) {
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || '{{amount}} €';

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = typeof precision === 'undefined' ? 2 : precision;
    thousands = typeof thousands === 'undefined' ? ',' : thousands;
    decimal = typeof decimal === 'undefined' ? '.' : decimal;

    if (Number.isNaN(number) || number === null) return 0;

    const fixed = (number / 100).toFixed(precision);
    const parts = fixed.split('.');
    const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    const centsAmount = parts[1] ? decimal + parts[1] : '';

    return dollarsAmount + centsAmount;
  }

  const match = formatString.match(placeholderRegex);
  const key = match ? match[1] : 'amount';
  let value;

  switch (key) {
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    default:
      value = formatWithDelimiters(cents, 2);
  }

  return match ? formatString.replace(placeholderRegex, value) : `${value} €`;
}

class GiftPouch extends HTMLElement {
  connectedCallback() {
    this.checkbox = this.querySelector('.gift-pouch__checkbox');
    this.panel = this.querySelector('.gift-pouch__panel');
    this.slider = this.querySelector('.gift-pouch__slider');
    this.errorEl = this.querySelector('.gift-pouch__error');
    this.selectionEl = this.querySelector('.gift-pouch__selection');
    this.cards = Array.from(this.querySelectorAll('.gift-pouch__card'));
    this.mainForm = this.closest('product-form');
    this.productForm = this.mainForm ? this.mainForm.querySelector('.js-product-form') : null;

    if (!this.checkbox || this.cards.length === 0) return;

    this.checkbox.addEventListener('change', () => this.togglePanel());
    this.cards.forEach((card) => {
      card.addEventListener('click', () => this.selectCard(card));
    });

    this.initSlider();

    this.boundHandleMainAdd = this.handleMainAdd.bind(this);
    document.addEventListener('on:cart:add', this.boundHandleMainAdd);

    // Switching a variant of this product re-renders its add-to-cart button
    // label (variant-picker.js), wiping our "(+X €)" suffix - re-apply it.
    if (this.mainForm) {
      this.mainForm.addEventListener('on:variant:change', () => this.updateAddToCartTotal());
    }
  }

  disconnectedCallback() {
    document.removeEventListener('on:cart:add', this.boundHandleMainAdd);
  }

  get selectedCard() {
    return this.cards.find((card) => card.classList.contains('is-selected')) || null;
  }

  togglePanel() {
    const open = this.checkbox.checked;
    this.panel.hidden = !open;
    this.classList.toggle('is-open', open);

    if (open) {
      // Pre-picking the first pouch means a shopper who just ticks the box and
      // hits "In den Warenkorb" still gets one, instead of silently nothing.
      if (!this.selectedCard && this.dataset.autoSelectFirst) this.selectCard(this.cards[0], true);
    } else {
      this.clearSelection();
    }

    this.syncGroupInput();
    this.updateSelectionSummary();
    this.updateAddToCartTotal();
  }

  clearSelection() {
    this.cards.forEach((card) => {
      card.classList.remove('is-selected');
      card.setAttribute('aria-checked', 'false');
    });
  }

  selectCard(card, silent) {
    if (card.classList.contains('is-added')) return;

    const wasSelected = card.classList.contains('is-selected');
    this.clearSelection();

    // Clicking the already-selected card unpicks it (and unticks the box),
    // so a pouch can be removed without hunting for another control.
    if (wasSelected && !silent) {
      this.checkbox.checked = false;
      this.togglePanel();
      return;
    }

    card.classList.add('is-selected');
    card.setAttribute('aria-checked', 'true');

    if (!this.checkbox.checked) {
      this.checkbox.checked = true;
      this.panel.hidden = false;
      this.classList.add('is-open');
    }

    if (!silent) card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });

    this.syncGroupInput();
    this.updateSelectionSummary();
    this.updateAddToCartTotal();
  }

  /**
   * Repeats the picked pouch (title and price) above the slider, so the
   * choice is readable without scanning the cards for the tick.
   */
  updateSelectionSummary() {
    if (!this.selectionEl) return;

    const card = this.selectedCard;
    this.selectionEl.hidden = !card;
    if (!card) return;

    const price = parseInt(card.dataset.price, 10) || 0;
    this.selectionEl.querySelector('.gift-pouch__selection-label').textContent = this.dataset.labelSelected;
    this.selectionEl.querySelector('.gift-pouch__selection-value').textContent =
      `${card.dataset.title} (${formatMoney(price, this.dataset.moneyFormat)})`;
  }

  /**
   * Keeps a hidden "_gift_pouch_group" input in the main product's form for
   * exactly as long as a pouch is selected, so FormData in ProductForm picks
   * it up. The id is minted per selection and reused for the pouch request.
   */
  syncGroupInput() {
    if (!this.productForm) return;

    let input = this.productForm.querySelector(`input[name="${GROUP_PROPERTY}"]`);

    if (!this.selectedCard) {
      if (input) input.remove();
      return;
    }

    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = GROUP_PROPERTY;
      input.value = `gp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      this.productForm.appendChild(input);
    }
  }

  get groupId() {
    const input = this.productForm && this.productForm.querySelector(`input[name="${GROUP_PROPERTY}"]`);
    return input ? input.value : null;
  }

  /**
   * Pagination dots (click to jump, highlight whatever's in view) plus
   * click-and-drag scrolling for a mouse - touch swiping already works from
   * the CSS scroll-snap alone.
   */
  initSlider() {
    if (!this.slider) return;

    const dotsWrap = this.querySelector('.gift-pouch__dots');

    if (dotsWrap && this.cards.length > 1) {
      this.cards.forEach((card, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'gift-pouch__dot';
        dot.tabIndex = -1;
        dot.addEventListener('click', () => {
          this.slider.scrollTo({ left: card.offsetLeft - this.slider.offsetLeft, behavior: 'smooth' });
        });
        dotsWrap.appendChild(dot);
      });

      const dots = Array.from(dotsWrap.children);
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const index = this.cards.indexOf(entry.target);
            if (index === -1) return;
            dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
          });
        },
        { root: this.slider, threshold: 0.6 }
      );

      this.cards.forEach((card) => observer.observe(card));
    }

    if (this.cards.length > 1 && window.matchMedia('(pointer: fine)').matches) {
      this.initSliderDrag();
    }
  }

  initSliderDrag() {
    let isDown = false;
    let startX = 0;
    let startScrollLeft = 0;
    let moved = false;

    this.slider.classList.add('is-grabbable');

    const stopDragging = () => {
      isDown = false;
      this.slider.classList.remove('is-grabbing');
    };

    this.slider.addEventListener('mousedown', (evt) => {
      isDown = true;
      moved = false;
      startX = evt.pageX;
      startScrollLeft = this.slider.scrollLeft;
      this.slider.classList.add('is-grabbing');
    });
    this.slider.addEventListener('mouseup', stopDragging);
    this.slider.addEventListener('mouseleave', stopDragging);
    this.slider.addEventListener('mousemove', (evt) => {
      if (!isDown) return;
      evt.preventDefault();
      if (Math.abs(evt.pageX - startX) > 4) moved = true;
      this.slider.scrollLeft = startScrollLeft - (evt.pageX - startX);
    });

    // A drag that ends on top of a card must not count as picking it.
    this.slider.addEventListener(
      'click',
      (evt) => {
        if (!moved) return;
        evt.preventDefault();
        evt.stopPropagation();
        moved = false;
      },
      true
    );
  }

  /**
   * Appends the selected pouch's price to this product's own add-to-cart
   * button as "(+9,00 €)".
   */
  updateAddToCartTotal() {
    if (!this.mainForm) return;

    const addBtn = this.mainForm.querySelector('.add-to-cart[name="add"]');
    if (!addBtn || !addBtn.dataset.addToCartText) return;

    const card = this.selectedCard;
    const price = card ? parseInt(card.dataset.price, 10) || 0 : 0;

    addBtn.textContent =
      price > 0
        ? `${addBtn.dataset.addToCartText} (+${formatMoney(price, this.dataset.moneyFormat)})`
        : addBtn.dataset.addToCartText;
  }

  /**
   * Fires whenever any product-form on the page successfully adds to cart.
   * Only react to this product's own form.
   */
  handleMainAdd(evt) {
    if (this.mainForm && evt.target !== this.mainForm) return;
    this.addSelectedPouch();
  }

  showError() {
    if (!this.errorEl) return;
    this.errorEl.hidden = false;
    this.errorEl.textContent = this.dataset.labelError;
  }

  /**
   * Shows the same toast the regular add-to-cart button shows (they stack -
   * see added-notification.js). No-op in "drawer" mode: the main product's
   * own add already opens the drawer with the refreshed contents.
   */
  notifyAdded(title) {
    if (!window.theme || !window.theme.settings || window.theme.settings.afterAddToCart !== 'notification') return;

    const template = document.getElementById('AddedNotification');
    if (!template) return;

    const notification = template.content.firstElementChild.cloneNode(true);
    notification.dataset.productTitle = title;

    const container = document.querySelector('.pageheader--sticky') || document.querySelector('body');
    container.appendChild(notification);
  }

  async addSelectedPouch() {
    const card = this.selectedCard;
    if (!card || !this.dataset.cartAddUrl) return;

    const id = parseInt(card.dataset.variantId, 10);
    if (Number.isNaN(id)) return;

    const item = { id, quantity: 1 };
    if (this.groupId) item.properties = { _gift_pouch_for: this.groupId };

    if (this.errorEl) this.errorEl.hidden = true;

    try {
      const response = await fetch(this.dataset.cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ items: [item] })
      });
      const data = await response.json();

      if (!response.ok) {
        const message =
          data.description ||
          (data.errors && typeof data.errors === 'object' ? Object.values(data.errors).join(', ') : null) ||
          response.statusText;
        throw new Error(message);
      }

      document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
      this.notifyAdded(card.dataset.title);

      // Reset back to the unticked state, so a second add-to-cart on the same
      // page doesn't quietly add another pouch nobody asked for.
      this.checkbox.checked = false;
      this.togglePanel();
    } catch (error) {
      this.showError();
    }
  }
}

window.customElements.define('gift-pouch', GiftPouch);
